/**
 * Shared registry for the WFT site.
 * Mirrors the product's frontend/src/lib/{sections,sources,format}.ts.
 *
 * This module is pure data plus two derivations. It performs no I/O: the site
 * is a design surface for a terminal whose API is not reachable from here, and
 * "not connected" is a first-class state rather than an error.
 */

export type ClusterId = 'tape' | 'context' | 'engine' | 'desk' | 'system';

export type PanelKind =
  | 'board'
  | 'chart'
  | 'curve'
  | 'fan'
  | 'form'
  | 'grid'
  | 'heat'
  | 'kpi'
  | 'ladder'
  | 'list'
  | 'map'
  | 'matrix'
  | 'network'
  | 'reader'
  | 'scatter'
  | 'ticker'
  | 'timeline'
  | 'tree'
  | 'treemap';

export interface NavItem {
  label: string;
  href: string;
  key: string;
}

export interface FooterLink {
  label: string;
  href: string;
}

export interface FooterCol {
  title: string;
  links: FooterLink[];
}

export interface Session {
  city: string;
  code: string;
  tz: string;
  /** Local opening hour, 24h. */
  open: number;
  /** Local closing hour, 24h. */
  close: number;
}

export interface SessionRow {
  city: string;
  code: string;
  time: string;
  hm: string;
  state: '' | 'open' | 'closed' | 'weekend';
  color: string;
}

export interface Cluster {
  label: string;
  blurb: string;
}

export interface Source {
  label: string;
  cadence: string;
  key: string;
  redist: 'yes' | 'display-only';
  tier: string;
  feeds: string;
  note: string;
}

export interface Panel {
  title: string;
  kind: PanelKind;
  sources: string[];
  wireIn?: string | null;
  needs?: string[] | null;
  note?: string | null;
}

export interface Section {
  id: string;
  mnemonic: string;
  title: string;
  cluster: ClusterId;
  /** `g`-leader key that jumps to this section in the terminal. */
  leader: string;
  /** False when no connector exists for it yet. */
  live: boolean;
  purpose: string;
  panels: Panel[];
}

export interface SourceState {
  name: string;
  color: string;
  fill: string;
  ring: string;
  body: string;
}

export interface PanelDot {
  title: string;
  fill: string;
  ring: string;
  opacity: number;
}

export interface PanelView {
  title: string;
  index: number;
  kind: PanelKind;
  dots: PanelDot[];
  stateLabel: string;
  isSessions: boolean;
  isSources: boolean;
  isNC: boolean;
  ncLine: string;
  needsLine: string;
  connector: string;
}

export const NAV: NavItem[] = [
  { label: 'Platform', href: '/platform', key: 'platform' },
  { label: 'Engine', href: '/engine', key: 'engine' },
  { label: 'Data', href: '/data', key: 'data' },
  { label: 'Track record', href: '/record', key: 'record' },
  { label: 'Design', href: '/design', key: 'design' },
  { label: 'Access', href: '/access', key: 'access' },
  { label: 'FAQ', href: '/faq', key: 'faq' },
];

export const FOOTER_COLS: FooterCol[] = [
  {
    title: 'Product',
    links: [
      { label: 'Platform', href: '/platform' },
      { label: 'Engine', href: '/engine' },
      { label: 'Track record', href: '/record' },
      { label: 'Design system', href: '/design' },
    ],
  },
  {
    title: 'Data',
    links: [
      { label: 'Source registry', href: '/data' },
      { label: 'Source states', href: '/data' },
      { label: 'FAQ', href: '/faq' },
    ],
  },
  {
    title: 'Run it',
    links: [
      { label: 'Access', href: '/access' },
      { label: 'Sign in', href: '/login' },
      { label: 'Open terminal', href: '/cockpit' },
    ],
  },
];

export const SESSIONS: Session[] = [
  { city: 'Sydney', code: 'SYDNEY', tz: 'Australia/Sydney', open: 10, close: 16 },
  { city: 'Tokyo', code: 'TOKYO', tz: 'Asia/Tokyo', open: 9, close: 15 },
  { city: 'Mumbai', code: 'MUMBAI', tz: 'Asia/Kolkata', open: 9, close: 15 },
  { city: 'London', code: 'LONDON', tz: 'Europe/London', open: 8, close: 16 },
  { city: 'New York', code: 'NEW YORK', tz: 'America/New_York', open: 9, close: 16 },
];

export function sessionRows(now: Date | null): SessionRow[] {
  return SESSIONS.map((s) => {
    if (!now)
      return {
        city: s.city,
        code: s.code,
        time: '--:--:--',
        hm: '--:--',
        state: '',
        color: 'var(--wft-ink-3)',
      };
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: s.tz,
      weekday: 'short',
      hour: 'numeric',
      hour12: false,
    }).formatToParts(now);
    const weekday = parts.find((p) => p.type === 'weekday')?.value ?? '';
    const hour = Number(parts.find((p) => p.type === 'hour')?.value ?? '0');
    const state =
      weekday === 'Sat' || weekday === 'Sun'
        ? 'weekend'
        : hour >= s.open && hour < s.close
          ? 'open'
          : 'closed';
    const time = new Intl.DateTimeFormat('en-GB', {
      timeZone: s.tz,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).format(now);
    return {
      city: s.city,
      code: s.code,
      time,
      hm: time.slice(0, 5),
      state,
      color:
        state === 'open'
          ? 'var(--wft-live)'
          : state === 'weekend'
            ? 'var(--wft-unbound)'
            : 'var(--wft-ink-3)',
    };
  });
}

export const CLUSTERS: Record<ClusterId, Cluster> = {
  tape: { label: 'Tape', blurb: "What the world's money is doing right now" },
  context: { label: 'Context', blurb: "Why it's doing that" },
  engine: { label: 'Engine', blurb: 'What GodEngine thinks happens next' },
  desk: { label: 'Desk', blurb: 'Your book' },
  system: { label: 'System', blurb: 'Where the numbers come from' },
};

export const SOURCES: Source[] = [
  {
    label: 'yfinance',
    cadence: '1d',
    key: '—',
    redist: 'display-only',
    tier: 'free · keyless',
    feeds: 'Markets · Commodities · Contagion',
    note: 'End-of-day. Yahoo terms prohibit redistribution.',
  },
  {
    label: 'CoinGecko',
    cadence: '60s',
    key: '—',
    redist: 'yes',
    tier: 'free · keyless',
    feeds: 'Crypto · Cockpit · Contagion',
    note: 'Prices, not order books.',
  },
  {
    label: 'FRED',
    cadence: '6h',
    key: 'FRED_API_KEY',
    redist: 'yes',
    tier: 'free · key',
    feeds: 'Rates · Macro · Cockpit',
    note: 'Instant signup. Unlocks Rates, Macro and parts of Cockpit.',
  },
  {
    label: 'SEC EDGAR',
    cadence: '4h',
    key: 'SEC_CONTACT',
    redist: 'yes',
    tier: 'free · key',
    feeds: 'Filings',
    note: 'Public domain. Needs only a contact string for the fair-use UA.',
  },
  {
    label: 'GDELT',
    cadence: '15m',
    key: '—',
    redist: 'yes',
    tier: 'free · keyless',
    feeds: 'Wires · Cockpit',
    note: 'Article stream and entity extraction.',
  },
  {
    label: 'RSS wires',
    cadence: '5m',
    key: '—',
    redist: 'display-only',
    tier: 'free · keyless',
    feeds: 'Wires · Cockpit',
    note: 'Publisher terms vary; display-only.',
  },
  {
    label: 'Frankfurter FX',
    cadence: '1d',
    key: '—',
    redist: 'yes',
    tier: 'free · keyless',
    feeds: 'FX · Cockpit',
    note: 'Reference rates.',
  },
  {
    label: 'ECB',
    cadence: '1d',
    key: '—',
    redist: 'yes',
    tier: 'free · keyless',
    feeds: 'FX · Rates',
    note: 'Euro-area rates and curves.',
  },
  {
    label: 'World Bank',
    cadence: '7d',
    key: '—',
    redist: 'yes',
    tier: 'free · keyless',
    feeds: 'Macro',
    note: 'Macro indicators by country.',
  },
  {
    label: 'IMF',
    cadence: '7d',
    key: '—',
    redist: 'yes',
    tier: 'free · keyless',
    feeds: 'Macro',
    note: 'Release calendar and vintages.',
  },
  {
    label: 'OECD',
    cadence: '7d',
    key: '—',
    redist: 'yes',
    tier: 'free · keyless',
    feeds: 'Macro',
    note: 'Comparable macro series.',
  },
  {
    label: 'US Treasury',
    cadence: '1d',
    key: '—',
    redist: 'yes',
    tier: 'free · keyless',
    feeds: 'Rates',
    note: 'Par yield curve.',
  },
  {
    label: 'Econ calendar',
    cadence: '1h',
    key: '—',
    redist: 'display-only',
    tier: 'free',
    feeds: 'Calendar · FX · Cockpit',
    note: 'Scheduled events; consensus is not included.',
  },
  {
    label: 'GodEngine',
    cadence: '1h',
    key: '—',
    redist: 'yes',
    tier: 'engine',
    feeds: 'Every forecast column, Forecast Lab, Record, Divergence',
    note: 'TimesFM 2.5 forecasts and swarm scenario resolve.',
  },
  {
    label: 'Polygon',
    cadence: '60s',
    key: 'POLYGON_API_KEY',
    redist: 'display-only',
    tier: 'paid · optional',
    feeds: 'Markets (intraday)',
    note: 'Without it, those panels are honestly empty.',
  },
  {
    label: 'Finnhub',
    cadence: '60s',
    key: 'FINNHUB_API_KEY',
    redist: 'display-only',
    tier: 'paid · optional',
    feeds: 'Markets · Filings',
    note: 'Optional. Unbound is a supported state.',
  },
  {
    label: 'Twelve Data',
    cadence: '60s',
    key: 'TWELVEDATA_API_KEY',
    redist: 'display-only',
    tier: 'paid · optional',
    feeds: 'Markets · FX',
    note: 'Optional intraday coverage.',
  },
];

const P = (
  title: string,
  kind: PanelKind,
  sources?: string[] | null,
  wireIn?: string | null,
  needs?: string[] | null,
  note?: string | null
): Panel => ({ title, kind, sources: sources || [], wireIn, needs, note });

export const SECTIONS: Section[] = [
  {
    id: 'cockpit',
    mnemonic: 'COK',
    title: 'Cockpit',
    cluster: 'tape',
    leader: 'c',
    live: true,
    purpose: 'The one screen you leave open all day',
    panels: [
      P('Global tape', 'ticker', ['yfinance', 'CoinGecko', 'Frankfurter FX']),
      P('Cross-asset heat', 'heat', ['yfinance', 'CoinGecko', 'FRED', 'Frankfurter FX']),
      P("Engine's loudest calls", 'fan', ['GodEngine']),
      P(
        'Wires',
        'list',
        ['GDELT', 'RSS wires'],
        'wft/sources/gdelt.py, wft/sources/rss.py',
        ['article stream', 'entity extraction'],
        'Both are keyless; neither is written yet.'
      ),
      P(
        'Sessions',
        'kpi',
        [],
        'client-side clock',
        null,
        'Derived from the browser clock and exchange hours.'
      ),
      P('Source health', 'board', [], 'wft/api/routes/sources.py'),
      P('Next events', 'list', ['Econ calendar']),
    ],
  },
  {
    id: 'markets',
    mnemonic: 'MKT',
    title: 'Markets',
    cluster: 'tape',
    leader: 'm',
    live: true,
    purpose: 'Equities and indices — the workhorse grid',
    panels: [
      P('Instruments', 'grid', ['yfinance', 'GodEngine']),
      P('Price + forecast', 'fan', ['yfinance', 'GodEngine']),
      P('Sector treemap', 'treemap', ['yfinance']),
      P('Breadth', 'kpi', ['yfinance']),
    ],
  },
  {
    id: 'fx',
    mnemonic: 'FX',
    title: 'FX',
    cluster: 'tape',
    leader: 'f',
    live: true,
    purpose: 'Cross-rate matrix and carry',
    panels: [
      P('Cross rates', 'matrix', ['Frankfurter FX', 'ECB']),
      P('Pair + forecast', 'fan', ['Frankfurter FX', 'GodEngine']),
      P('Rate differentials', 'grid', ['FRED', 'ECB']),
      P('Central bank calendar', 'list', ['Econ calendar']),
    ],
  },
  {
    id: 'rates',
    mnemonic: 'RTS',
    title: 'Rates',
    cluster: 'tape',
    leader: 'r',
    live: true,
    purpose: 'Sovereign curves and what they imply',
    panels: [
      P('Yield curves', 'curve', ['FRED', 'US Treasury', 'ECB']),
      P('2s10s / 3m10y', 'chart', ['FRED', 'US Treasury']),
      P('Tenor grid', 'grid', ['FRED', 'US Treasury']),
      P('Real rates', 'chart', ['FRED']),
      P('Curve forecast', 'fan', ['FRED', 'GodEngine']),
    ],
  },
  {
    id: 'commodities',
    mnemonic: 'CMDT',
    title: 'Commodities',
    cluster: 'tape',
    leader: 'o',
    live: true,
    purpose: 'Energy, metals and agriculture',
    panels: [
      P('Complexes', 'grid', ['yfinance', 'GodEngine']),
      P('Spot + forecast', 'fan', ['yfinance', 'GodEngine']),
      P(
        'Term structure',
        'chart',
        [],
        'wft/sources/futures_curve.py',
        ['contract months', 'settlement price', 'open interest'],
        'Contango and backwardation need a futures-curve feed; no free source provides one.'
      ),
      P('Commodity ↔ FX', 'matrix', ['yfinance', 'Frankfurter FX']),
    ],
  },
  {
    id: 'crypto',
    mnemonic: 'CRY',
    title: 'Crypto',
    cluster: 'tape',
    leader: 'y',
    live: true,
    purpose: 'The 24/7 book',
    panels: [
      P('Top 100', 'grid', ['CoinGecko', 'GodEngine']),
      P('Pair + forecast', 'fan', ['CoinGecko', 'GodEngine']),
      P('Dominance', 'chart', ['CoinGecko']),
      P(
        'Order book depth',
        'ladder',
        [],
        'wft/sources/exchange_l2.py',
        ['bid/ask ladder', 'size at level', 'funding rate', 'open interest'],
        'CoinGecko publishes prices, not books. Level 2 needs a direct exchange feed.'
      ),
    ],
  },
  {
    id: 'macro',
    mnemonic: 'MAC',
    title: 'Macro',
    cluster: 'context',
    leader: 'a',
    live: true,
    purpose: 'The economy, country by country',
    panels: [
      P('World', 'map', ['World Bank', 'FRED']),
      P('Country detail', 'grid', ['World Bank', 'FRED']),
      P('Series compare + forecast', 'fan', ['FRED', 'GodEngine']),
      P('Release vs forecast', 'grid', [], 'wft/sources/imf.py, wft/sources/oecd.py', [
        'scheduled release calendar',
        'vintage data',
      ]),
    ],
  },
  {
    id: 'wires',
    mnemonic: 'WIR',
    title: 'Wires',
    cluster: 'context',
    leader: 'w',
    live: true,
    purpose: 'Financial newsflow, machine-read',
    panels: [
      P('Wire', 'list', ['GDELT', 'RSS wires']),
      P('Entities mentioned', 'grid', ['GDELT']),
      P('Tone and volume', 'chart', ['GDELT']),
      P('Source mix', 'kpi', ['GDELT', 'RSS wires']),
    ],
  },
  {
    id: 'filings',
    mnemonic: 'FIL',
    title: 'Filings',
    cluster: 'context',
    leader: 'i',
    live: true,
    purpose: 'Primary documents, unfiltered',
    panels: [
      P('EDGAR feed', 'grid', ['SEC EDGAR']),
      P('Filing', 'reader', ['SEC EDGAR']),
      P('Form frequency', 'chart', ['SEC EDGAR']),
      P('XBRL facts', 'grid', [], 'wft/sources/xbrl.py', [
        'XBRL instance parsing',
        'us-gaap taxonomy mapping',
      ]),
    ],
  },
  {
    id: 'calendar',
    mnemonic: 'CAL',
    title: 'Calendar',
    cluster: 'context',
    leader: 'l',
    live: true,
    purpose: "What's scheduled to move things",
    panels: [
      P('This week', 'grid', ['Econ calendar']),
      P(
        'Event detail',
        'kpi',
        [],
        'wft/sources/consensus.py',
        ['analyst consensus', 'prior revisions'],
        'Prior and actual come from FRED. Consensus needs a survey provider.'
      ),
      P('Countdown', 'list', ['Econ calendar']),
      P('Surprise vs reaction', 'scatter', ['Econ calendar', 'FRED']),
    ],
  },
  {
    id: 'screener',
    mnemonic: 'SCR',
    title: 'Screener',
    cluster: 'context',
    leader: 's',
    live: true,
    purpose: 'Ask the whole universe a question',
    panels: [
      P('Query', 'form', [], 'wft/api/routes/screener.py'),
      P('Results', 'grid', ['yfinance', 'CoinGecko', 'FRED', 'GodEngine']),
      P('Saved screens', 'list', [], 'localStorage at M1'),
      P('Distribution', 'chart', ['yfinance']),
    ],
  },
  {
    id: 'forecast',
    mnemonic: 'FCST',
    title: 'Forecast Lab',
    cluster: 'engine',
    leader: 'e',
    live: true,
    purpose: 'Forecast anything, with quantiles and a baseline',
    panels: [
      P('Series', 'form', ['yfinance', 'FRED', 'CoinGecko']),
      P('Forecast', 'fan', ['GodEngine']),
      P('Horizon and transform', 'form', [], 'client-side'),
      P('Provenance', 'kpi', ['GodEngine']),
      P('Runs for this series', 'grid', ['GodEngine']),
    ],
  },
  {
    id: 'record',
    mnemonic: 'REC',
    title: 'Track Record',
    cluster: 'engine',
    leader: 'k',
    live: true,
    purpose: "The engine's report card — resolved forecasts, scored",
    panels: [
      P('Score', 'kpi', ['GodEngine']),
      P('Forecast ledger', 'grid', ['GodEngine']),
      P('Calibration', 'chart', ['GodEngine']),
      P('Skill by horizon', 'chart', ['GodEngine']),
    ],
  },
  {
    id: 'divergence',
    mnemonic: 'DIV',
    title: 'Divergence',
    cluster: 'engine',
    leader: 'd',
    live: true,
    purpose: 'Where the engine most disagrees with the alternative',
    panels: [
      P('Disagreement', 'grid', ['GodEngine', 'yfinance']),
      P('Observed / engine / baseline', 'fan', ['GodEngine', 'yfinance']),
      P(
        'Does disagreeing pay?',
        'scatter',
        ['GodEngine'],
        null,
        null,
        'Populates once forecasts begin resolving.'
      ),
      P(
        'Engine vs analyst consensus',
        'chart',
        [],
        'wft/sources/consensus.py',
        ['analyst estimates', 'survey medians'],
        'Engine-vs-baseline divergence is live; engine-vs-crowd needs an estimates provider.'
      ),
    ],
  },
  {
    id: 'scenarios',
    mnemonic: 'SCN',
    title: 'Scenarios',
    cluster: 'engine',
    leader: 'n',
    live: false,
    purpose: 'Branching futures with probability mass',
    panels: [
      P('Scenario tree', 'tree', [], 'wft/godengine/client.py → /api/resolve/stream', [
        'scenario branches',
        'branch probabilities',
      ]),
      P('Branch detail', 'kpi', [], 'wft/godengine/client.py', ['assumptions', 'triggers']),
      P('Probability mass over time', 'chart', [], 'wft/godengine/client.py'),
      P('Which branch are we in?', 'list', [], 'wft/godengine/client.py'),
    ],
  },
  {
    id: 'sim',
    mnemonic: 'SIM',
    title: 'Simulation',
    cluster: 'engine',
    leader: 'u',
    live: false,
    purpose: 'Push a shock, watch it propagate',
    panels: [
      P('Shock composer', 'form', [], 'wft/api/routes/sim.py (WebSocket)'),
      P('Propagation', 'network', [], 'wft/api/routes/sim.py', [
        'asset linkage graph',
        'shock transmission weights',
      ]),
      P('Impact by asset', 'chart', [], 'wft/api/routes/sim.py'),
      P('Sensitivity', 'chart', [], 'wft/api/routes/sim.py'),
      P('Tick timeline', 'timeline', [], 'wft/api/routes/sim.py'),
    ],
  },
  {
    id: 'contagion',
    mnemonic: 'CTG',
    title: 'Contagion',
    cluster: 'engine',
    leader: 'g',
    live: true,
    purpose: 'Correlation topology of everything',
    panels: [
      P('Correlation', 'matrix', ['yfinance', 'CoinGecko']),
      P('Linkage graph', 'network', ['yfinance', 'CoinGecko']),
      P('Rolling correlation', 'chart', ['yfinance']),
      P('By regime', 'grid', ['yfinance']),
      P('Systemic risk', 'kpi', ['yfinance', 'CoinGecko']),
    ],
  },
  {
    id: 'regimes',
    mnemonic: 'REG',
    title: 'Regimes',
    cluster: 'engine',
    leader: 'x',
    live: false,
    purpose: 'What kind of market this is',
    panels: [
      P('Regime timeline', 'timeline', [], 'wft/api/routes/regimes.py', [
        'regime classifier',
        'labelled history',
      ]),
      P('Feature contribution', 'chart', [], 'wft/api/routes/regimes.py'),
      P('Transition matrix', 'matrix', [], 'wft/api/routes/regimes.py'),
      P('Performance by regime', 'grid', [], 'wft/api/routes/regimes.py'),
      P('Current regime', 'kpi', [], 'wft/api/routes/regimes.py'),
    ],
  },
  {
    id: 'watch',
    mnemonic: 'WCH',
    title: 'Watchlists',
    cluster: 'desk',
    leader: 't',
    live: true,
    purpose: 'Your lists, with engine columns on',
    panels: [
      P('Lists', 'list', [], 'localStorage at M1; wft/api/routes/watch.py at M2'),
      P('Members', 'grid', ['yfinance', 'CoinGecko', 'GodEngine']),
      P('Selected', 'fan', ['yfinance', 'GodEngine']),
    ],
  },
  {
    id: 'alerts',
    mnemonic: 'ALR',
    title: 'Alerts',
    cluster: 'desk',
    leader: 'p',
    live: true,
    purpose: 'Armed conditions, including engine-conditioned ones',
    panels: [
      P('New alert', 'form', [], 'localStorage at M1'),
      P('Armed', 'grid', ['yfinance', 'CoinGecko', 'GodEngine']),
      P(
        'Recently fired',
        'list',
        [],
        'wft/api/routes/alerts.py at M2',
        null,
        'Client-evaluated on poll at M1; server push needs a worker.'
      ),
    ],
  },
  {
    id: 'sources',
    mnemonic: 'SRC',
    title: 'Sources',
    cluster: 'system',
    leader: 'v',
    live: true,
    purpose: 'Every connector, its state, and what it feeds',
    panels: [
      P('Connectors', 'board', [], 'wft/api/routes/sources.py'),
      P('Detail', 'kpi', [], 'wft/api/routes/sources.py'),
      P('Sections fed', 'list', [], 'client-side'),
    ],
  },
  {
    id: 'settings',
    mnemonic: 'SET',
    title: 'Settings',
    cluster: 'system',
    leader: 'z',
    live: true,
    purpose: 'Theme, density, timezone, keymap',
    panels: [
      P('Appearance', 'form', [], 'client-side'),
      P('Data and units', 'form', [], 'client-side'),
      P('Keyboard', 'list', [], 'client-side'),
    ],
  },
];

export const STATES: SourceState[] = [
  {
    name: 'live',
    color: 'var(--wft-live)',
    fill: 'var(--wft-live)',
    ring: 'none',
    body: 'Fresh within one expected cadence.',
  },
  {
    name: 'delayed',
    color: 'var(--wft-delayed)',
    fill: 'var(--wft-delayed)',
    ring: 'none',
    body: 'Inherently behind real time — end-of-day, +15m. Normal, not an error.',
  },
  {
    name: 'stale',
    color: 'var(--wft-stale)',
    fill: 'var(--wft-stale)',
    ring: 'none',
    body: 'Older than twice the cadence. Values lose their colour.',
  },
  {
    name: 'partial',
    color: 'var(--wft-degraded)',
    fill: 'var(--wft-degraded)',
    ring: 'none',
    body: 'Some rows arrived. The missing ones hatch, never zero.',
  },
  {
    name: 'down',
    color: 'var(--wft-down-src)',
    fill: 'var(--wft-down-src)',
    ring: 'none',
    body: 'Last fetch failed. Last-good is retained but visibly demoted.',
  },
  {
    name: 'not connected',
    color: 'var(--wft-ink-2)',
    fill: 'transparent',
    ring: 'inset 0 0 0 1px var(--wft-unbound)',
    body: 'No connector exists yet. A first-class state, hollow rather than filled.',
  },
];

/** Panel view-model for the terminal recreation with no API reachable. */
export function panelView(panel: Panel, i: number): PanelView {
  const isSessions = panel.title === 'Sessions';
  const isSources = panel.title === 'Source health' || panel.title === 'Connectors';
  return {
    title: panel.title,
    index: i + 1,
    kind: panel.kind,
    dots: (panel.sources.length ? panel.sources : ['unbound']).slice(0, 4).map((label) => ({
      title: label + ' — not reachable from this demo',
      fill: 'transparent',
      ring: 'inset 0 0 0 1px var(--wft-unbound)',
      opacity: 0.6,
    })),
    stateLabel: isSessions ? 'DERIVED' : 'NOT CONNECTED',
    isSessions,
    isSources,
    isNC: !isSessions && !isSources,
    ncLine: panel.title + ' has no data source configured.',
    needsLine: panel.needs
      ? 'Needs: ' + panel.needs.join(' · ')
      : panel.note
        ? panel.note
        : panel.sources.length
          ? 'Would be fed by: ' + panel.sources.join(' · ')
          : '',
    connector:
      panel.wireIn || (panel.sources.length ? 'wft/api → ' + panel.sources.join(' · ') : 'wft/api'),
  };
}
